import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Paper } from '@mui/material';

// Hooks & services
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import useConfirmDialog from '../../../../hooks/useConfirmDialog';
import { deleteImage } from '../../../../services/images.service';

// Types
import { AllowedFileType, CONFIRM_DIALOG_FILE_DELETE, CONFIRM_DIALOG_IMAGE_DELETE, getFileTypeConfig } from '../../../../constants/Constants';
import { UploadImageRequest } from '../../../../requestModels/uploadImageRequest';
import { Image, ImageData } from '../../../../types/Images';

// Components
import { Spinner } from '../../Spinner';
import { ProgressItem } from './ProgressItem';
import { ImagesList } from './ImagesList';
import FilesList from './FilesList';
import { DeviceImportSummary } from '../../../../types/device-import-summary';

type UploadFormProps = UploadImageRequest & {
  fileType: AllowedFileType;
  onFilesUpdate?: (files: any[]) => void;
  uploadAsync?: (file: File, onUploadProgress: (progressEvent: ProgressEvent) => void) => Promise<any>;
};

export default function UploadForm(props: UploadFormProps) {
  const { notifyMessage } = useNotifyMessage();
  const { renderDialog, confirm } = useConfirmDialog(props.fileType === 'image' ? CONFIRM_DIALOG_IMAGE_DELETE : CONFIRM_DIALOG_FILE_DELETE);

  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [uploadedImages, setUploadedImages] = useState<ImageData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Remembers the original filename(s) of the last accepted drop, so we can
  // stamp them onto the server response (which doesn't carry the filename).
  const lastDroppedNamesRef = useRef<string[]>([]);

  const fileConfig = getFileTypeConfig(props.fileType);
  const maxSize = fileConfig.maxSizeMb * 1024 * 1024;

  useEffect(() => {
    if (props.files) {
      if (props.fileType === 'image') setUploadedImages(props.files as ImageData[]);
      else setUploadedFiles(props.files as any[]);
    }
  }, [props.files, props.fileType]);

  useEffect(() => {
    if (props.fileType === 'excel' && props.onFilesUpdate) {
      props.onFilesUpdate(uploadedFiles);
    }
  }, [uploadedFiles]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (props.fileType === 'excel' && acceptedFiles.length > 0) {
        // Names already in the form: completed uploads + files currently selected/uploading
        const existingNames = new Set(
          [...uploadedFiles.map((f) => (f.name ?? '').toLowerCase()), ...files.map((f) => f.name.toLowerCase())].filter(Boolean),
        );

        const duplicate = acceptedFiles.find((incoming) => existingNames.has(incoming.name.toLowerCase()));

        if (duplicate) {
          notifyMessage(`"${duplicate.name}" has already been uploaded. Please remove the existing file before uploading it again.`, 'error');
          return; // Block the drop entirely — don't add to files state
        }
      }

      lastDroppedNamesRef.current = acceptedFiles.map((f) => f.name);
      setFiles(acceptedFiles);
    },
    [uploadedFiles, files, notifyMessage, props.fileType],
  );

  const onDropRejected = (rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const rejectError = rejectedFiles[0];
      const { code, message } = rejectError.errors[0];
      if (code === 'file-too-large') {
        const fileSizeInBytes = rejectError.file.size;
        const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
        notifyMessage(
          `File is too large. Maximum allowed size is ${fileConfig.maxSizeMb} MB. Your file is ${fileSizeInMB.toFixed(2)} MB.`,
          'warning',
        );
      } else {
        notifyMessage(message, 'warning');
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: fileConfig.accept,
    onDropRejected,
    maxSize,
  });

  const handleImageUpload = async (images: Image[]) => {
    setUploadedImages((prevFiles) => {
      let currentFiles = [...prevFiles];
      currentFiles = [...currentFiles, ...images.map((img) => ({ image: img }))];
      return currentFiles;
    });
    notifyMessage('Image uploaded', 'success');
  };

  const handleFileUploaded = async (fileData: DeviceImportSummary) => {
    const [originalName] = lastDroppedNamesRef.current;

    setUploadedFiles((prevFiles) => {
      const incoming = Array.isArray(fileData) ? fileData : [fileData];
      // Stamp the original filename onto the response so dedupe + FilesList display work
      const named = incoming.map((f: any) => ({ ...f, name: f.name ?? originalName }));
      return [...prevFiles, ...named];
    });
  };

  const handleDelete = useCallback(
    async (file: any) => {
      const { accepted } = await confirm({});
      if (!accepted) return;

      if (file.image?.id) {
        const res: any = await deleteImage(file.image.id);
        if (res.status === 204) {
          setUploadedImages((prevFiles) => prevFiles.filter((v) => v.image?.id !== file.image.id));
          notifyMessage('Image deleted', 'success');
        } else {
          console.error('Unable to delete image', res);
          notifyMessage('Unable to delete image', 'error');
        }
      } else {
        setUploadedFiles((prevFiles) => prevFiles.filter((v) => v.name !== file.name));
        notifyMessage('File deleted', 'success');
      }
    },
    [confirm],
  );

  return (
    <>
      {renderDialog()}
      <Paper
        sx={{
          cursor: 'pointer',
          background: '#fafafa',
          color: '#bdbdbd',
          border: '1px dashed #ccc',
          '&:hover': { border: '1px solid #ccc' },
        }}
      >
        <div style={{ padding: '16px', height: 150 }} {...getRootProps()}>
          <input {...getInputProps()} />
          {isDragActive ? <p style={{ color: 'green' }}>Drop the files here...</p> : <p>Drag and drop some files here, or click to select files</p>}
        </div>
      </Paper>

      <ProgressItem
        param={{
          subjectId: props.subjectId,
          resourceType: props.resourceType,
          subjectIdentifier: props.subjectIdentifier,
          fileProcess: files,
        }}
        fileType={props.fileType}
        onUpdate={props.fileType === 'image' ? handleImageUpload : handleFileUploaded}
        setIsLoading={setIsLoading}
        uploadAsync={props.uploadAsync}
      />

      {props.fileType === 'image' ? (
        <ImagesList onDelete={handleDelete} images={uploadedImages} />
      ) : (
        <FilesList onDelete={handleDelete} files={uploadedFiles} />
      )}
      {props.fileType === 'excel' && <Spinner loading={isLoading ?? false} />}
    </>
  );
}
